import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/auth-context';
import { usePayment } from '@/contexts/PaymentContext';
import { apiFetch } from '@/lib/api';

interface UILock {
  setting_id: string;
  min_plan: string;
  restricted_mode: string;
  is_active: number;
}

export const useSettingLocks = (currentMode: string) => {
  const [locks, setLocks] = useState<UILock[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  const { user } = useAuth();
  const { subscription } = usePayment();

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const currentPlan = useMemo(() => {
    const normalize = (p: string) => {
      const up = (p || '').toUpperCase();
      if (up.includes('ELITE')) return 'ELITE';
      if (up.includes('PRO')) return 'PRO';
      if (up.includes('ENTERPRISE')) return 'ENTERPRISE';
      return 'FREE';
    };

    // 1. Beta Tester Priority (Unlocks everything, effectively ELITE+)
    if (user?.is_beta) {
      return 'ELITE';
    }

    // 2. Active Trial Priority (Unless specific sub is higher, but usually user.plan is updated by me.ts)
    // me.ts already handles the priority logic for user.plan, so we can trust it
    // IF it doesn't match 'FREE'.
    const userPlan = normalize(user?.plan || 'FREE');
    
    // 3. Subscription override (only if not FREE)
    if (subscription && (subscription.status === 'active' || subscription.status === 'cancelling')) {
      const subPlan = normalize(subscription.plan_id);
      
      // Compare userPlan (which might be trial) and subPlan
      const planHierarchy: Record<string, number> = { 'FREE': 0, 'PRO': 1, 'ELITE': 2, 'ENTERPRISE': 3 };
      if (planHierarchy[subPlan] > planHierarchy[userPlan]) {
         return subPlan;
      }
    }
    
    return userPlan;
  }, [subscription, user?.plan, user?.is_beta]);

  const fetchLocks = useCallback(async () => {
    if (!window.navigator.onLine) {
      setIsOnline(false);
      return;
    }

    try {
      const response = await apiFetch('/api/ui-locks');
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setLocks(data.filter((l: any) => l.is_active));
      setHasFetched(true);
    } catch (error) {
      console.error('Failed to fetch UI locks', error);
      // Stays hasFetched: false -> locked
    }
  }, []);

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  const getLockInfo = (settingId: string): { isLocked: boolean; reason?: string; min_plan?: string } => {
    // 1. OFFLINE CHECK
    if (!isOnline) {
      return { isLocked: true, reason: 'Internet connection required' };
    }

    // 2. DEFENSIVE DEFAULT: If no data fetched yet, lock everything
    if (!hasFetched) {
      return { isLocked: true, reason: 'Connecting to security engine...' };
    }

    const planHierarchy: Record<string, number> = { 
      'FREE': 0, 
      'PRO': 1, 
      'ELITE': 2,
      'ENTERPRISE': 3
    };

    // 3. SEARCH FOR RULE: Specific mode rule first, then global ALL rule
    let rule = locks.find(l => l.setting_id === settingId && l.restricted_mode === currentMode);
    if (!rule) {
      rule = locks.find(l => l.setting_id === settingId && l.restricted_mode === 'ALL');
    }

    // 4. IF NO RULE FOUND: Default to PRO (Security First) but still check plan
    // If the database has a missing key, we assume it is a Pro/Studio feature
    if (!rule) {
      const defaultMinPlan = 'PRO';
      const requiredLevel = planHierarchy[defaultMinPlan] || 0;
      const userLevel = planHierarchy[currentPlan] || 0;
      return { isLocked: userLevel < requiredLevel, min_plan: defaultMinPlan };
    }

    // 5. TIERED PLAN LOGIC
    const requiredLevel = planHierarchy[rule.min_plan] || 0;
    const userLevel = planHierarchy[currentPlan] || 0;

    // If setting is for FREE plan, it's always unlocked for everyone
    if (requiredLevel === 0) {
      return { isLocked: false, min_plan: rule.min_plan };
    }

    if (userLevel < requiredLevel) {
      return { 
        isLocked: true, 
        reason: `${rule.min_plan} plan or higher required`,
        min_plan: rule.min_plan
      };
    }

    // Unlocked!
    return { isLocked: false, min_plan: rule.min_plan };
  };

  const isSettingLocked = (id: string) => {
    // Basic settings are always unlocked 
    if (['reset_defaults', 'clear_local_saves', 'local_saves'].includes(id)) return false;
    
    // DEFENSIVE CHECK BEFORE FETCH COMPLETES:
    // If locks haven't been fetched from the server yet, we must defensively lock 
    // known premium features to prevent layout/lock flashing.
    if (!hasFetched) {
      const defaultPremiumFeatures: Record<string, string> = {
        ballPlacement: 'PRO',
        physics_advanced: 'PRO',
        outsideCollider: 'PRO',
        generalDisplay: 'PRO',
        visualStyles: 'PRO',
        cloud_storage: 'PRO',
        bulk_operations: 'ELITE',
      };
      
      const minPlan = defaultPremiumFeatures[id];
      if (minPlan) {
        const planHierarchy: Record<string, number> = { 
          'FREE': 0, 
          'PRO': 1, 
          'ELITE': 2,
          'ENTERPRISE': 3
        };
        const requiredLevel = planHierarchy[minPlan] || 0;
        const userLevel = planHierarchy[currentPlan] || 0;
        return userLevel < requiredLevel;
      }
      return false; // Fallback to unlocked for non-known premium features before fetch
    }

    const info = getLockInfo(id);
    
    // Explicitly allow if no rule found (the allow-list requested needs to be careful not to lock everything)
    // Actually, if a rule DOES NOT exist, we should probably default to UNLOCKED for safety
    // only lock if a rule exists and user doesn't meet it.
    
    let rule = locks.find(l => l.setting_id === id && l.restricted_mode === currentMode);
    if (!rule) {
      rule = locks.find(l => l.setting_id === id && l.restricted_mode === 'ALL');
    }
    
    if (!rule) return false; // NEW BEHAVIOR: Default to UNLOCKED if no rule defined

    return info.isLocked;
  };

  return { 
    getLockInfo, 
    isSettingLocked,
    locks, 
    refreshLocks: fetchLocks,
    hasFetched,
    isOnline
  };
};
